import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { UserProfile, UserSubscription, UserQuota, PricingTier } from '../types';
import { UsageType, USAGE_TO_MAX_KEY_MAP } from './quotaConfig';

/**
 * Firestore utility functions for managing user data, subscriptions, and quotas
 */

// Collection and document path helpers
const USERS_COLLECTION = 'users';
const SUBSCRIPTIONS_SUBCOLLECTION = 'subscriptions';
const QUOTAS_SUBCOLLECTION = 'quotas';
const USAGE_DOCUMENT = 'usage';

// User Profile Operations
export const createUserProfile = async (userProfile: Omit<UserProfile, 'createdAt'>): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const userRef = doc(db, USERS_COLLECTION, userProfile.uid);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    await setDoc(userRef, {
      ...userProfile,
      createdAt: serverTimestamp(),
    });
  }
};

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const userRef = doc(db, USERS_COLLECTION, uid);
  const userDoc = await getDoc(userRef);
  
  if (userDoc.exists()) {
    const data = userDoc.data();
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
    } as UserProfile;
  }
  
  return null;
};

export const updateUserProfile = async (uid: string, updates: Partial<UserProfile>): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const userRef = doc(db, USERS_COLLECTION, uid);
  await updateDoc(userRef, updates);
};

// Subscription Operations
export const createUserSubscription = async (uid: string, subscription: Omit<UserSubscription, 'createdAt'>): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const subscriptionRef = doc(db, USERS_COLLECTION, uid, SUBSCRIPTIONS_SUBCOLLECTION, subscription.id);
  await setDoc(subscriptionRef, {
    ...subscription,
    currentPeriodStart: subscription.currentPeriodStart ? Timestamp.fromDate(subscription.currentPeriodStart) : null,
    currentPeriodEnd: subscription.currentPeriodEnd ? Timestamp.fromDate(subscription.currentPeriodEnd) : null,
    createdAt: serverTimestamp(),
    canceledAt: subscription.canceledAt ? Timestamp.fromDate(subscription.canceledAt) : null,
    endedAt: subscription.endedAt ? Timestamp.fromDate(subscription.endedAt) : null,
  });
};

export const getUserSubscriptions = async (uid: string): Promise<UserSubscription[]> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const subscriptionsRef = collection(db, USERS_COLLECTION, uid, SUBSCRIPTIONS_SUBCOLLECTION);
  const querySnapshot = await getDocs(subscriptionsRef);
  
  return querySnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      currentPeriodStart: data.currentPeriodStart?.toDate() || null,
      currentPeriodEnd: data.currentPeriodEnd?.toDate() || null,
      createdAt: data.createdAt?.toDate() || null,
      canceledAt: data.canceledAt?.toDate() || null,
      endedAt: data.endedAt?.toDate() || null,
    } as UserSubscription;
  });
};

export const getActiveUserSubscription = async (uid: string): Promise<UserSubscription | null> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const subscriptionsRef = collection(db, USERS_COLLECTION, uid, SUBSCRIPTIONS_SUBCOLLECTION);
  const q = query(subscriptionsRef, where('status', 'in', ['active', 'trialing']));
  const querySnapshot = await getDocs(q);
  
  if (querySnapshot.empty) {
    return null;
  }
  
  const doc = querySnapshot.docs[0];
  const data = doc.data();
  return {
    ...data,
    currentPeriodStart: data.currentPeriodStart?.toDate() || null,
    currentPeriodEnd: data.currentPeriodEnd?.toDate() || null,
    createdAt: data.createdAt?.toDate() || null,
    canceledAt: data.canceledAt?.toDate() || null,
    endedAt: data.endedAt?.toDate() || null,
  } as UserSubscription;
};

export const updateUserSubscription = async (uid: string, subscriptionId: string, updates: Partial<UserSubscription>): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const subscriptionRef = doc(db, USERS_COLLECTION, uid, SUBSCRIPTIONS_SUBCOLLECTION, subscriptionId);
  
  // Convert Date objects to Timestamps for Firestore
  const firestoreUpdates: any = { ...updates };
  if (updates.currentPeriodStart !== undefined) {
    firestoreUpdates.currentPeriodStart = updates.currentPeriodStart ? Timestamp.fromDate(updates.currentPeriodStart) : null;
  }
  if (updates.currentPeriodEnd !== undefined) {
    firestoreUpdates.currentPeriodEnd = updates.currentPeriodEnd ? Timestamp.fromDate(updates.currentPeriodEnd) : null;
  }
  if (updates.canceledAt !== undefined) {
    firestoreUpdates.canceledAt = updates.canceledAt ? Timestamp.fromDate(updates.canceledAt) : null;
  }
  if (updates.endedAt !== undefined) {
    firestoreUpdates.endedAt = updates.endedAt ? Timestamp.fromDate(updates.endedAt) : null;
  }
  
  await updateDoc(subscriptionRef, firestoreUpdates);
};

// Quota Operations
export const getUserQuota = async (uid: string): Promise<UserQuota | null> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const quotaRef = doc(db, USERS_COLLECTION, uid, QUOTAS_SUBCOLLECTION, USAGE_DOCUMENT);
  const quotaDoc = await getDoc(quotaRef);
  
  if (quotaDoc.exists()) {
    const data = quotaDoc.data();
    
    // Return user quota with proper date conversion
    const userQuotaWithDate = {
      ...data,
      lastReset: data.lastReset?.toDate() || new Date(),
    } as UserQuota;
    
    return userQuotaWithDate;
  }
  
  return null;
};

export const createDefaultQuota = async (uid: string, tier: PricingTier = PricingTier.Free): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const quotaRef = doc(db, USERS_COLLECTION, uid, QUOTAS_SUBCOLLECTION, USAGE_DOCUMENT);
  const defaultQuota = getDefaultQuotaForTier(tier);
  
  await setDoc(quotaRef, {
    ...defaultQuota,
    lastReset: serverTimestamp(),
  });
};

export const updateUserQuota = async (uid: string, updates: Partial<UserQuota>): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const quotaRef = doc(db, USERS_COLLECTION, uid, QUOTAS_SUBCOLLECTION, USAGE_DOCUMENT);
  
  const firestoreUpdates: any = { ...updates };
  if (updates.lastReset) {
    firestoreUpdates.lastReset = Timestamp.fromDate(updates.lastReset);
  }
  
  await updateDoc(quotaRef, firestoreUpdates);
};

export const resetUserQuota = async (uid: string, tier: PricingTier): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not available');
  }
  const quotaRef = doc(db, USERS_COLLECTION, uid, QUOTAS_SUBCOLLECTION, USAGE_DOCUMENT);
  const defaultQuota = getDefaultQuotaForTier(tier);
  
  await updateDoc(quotaRef, {
    totalCanvases: 0,
    maxTotalCanvases: defaultQuota.maxTotalCanvases,
    threadStarters: 0,
    maxThreadStarters: defaultQuota.maxThreadStarters,
    fileAttachments: 0,
    maxFileAttachments: defaultQuota.maxFileAttachments,
    webSearches: 0,
    maxWebSearches: defaultQuota.maxWebSearches,
    totalThreads: 0,
    maxTotalThreads: defaultQuota.maxTotalThreads,
    monthlyChatMessages: 0,
    maxMonthlyChatMessages: defaultQuota.maxMonthlyChatMessages,
    resetPeriod: defaultQuota.resetPeriod,
    lastReset: serverTimestamp(),
  });
};

// Helper function to get default quota limits for each tier
export const getDefaultQuotaForTier = (tier: PricingTier): Omit<UserQuota, 'lastReset'> => {
  switch (tier) {
    case PricingTier.Free:
      return {
        totalCanvases: 0,
        maxTotalCanvases: 3,
        threadStarters: 0,
        maxThreadStarters: 3,
        fileAttachments: 0,
        maxFileAttachments: 3,
        webSearches: 0,
        maxWebSearches: 3,
        totalThreads: 0,
        maxTotalThreads: 20,
        monthlyChatMessages: 0,
        maxMonthlyChatMessages: 100,
        resetPeriod: 'monthly',
      };
    
    case PricingTier.Basic:
      return {
        totalCanvases: 0,
        maxTotalCanvases: 10,
        threadStarters: 0,
        maxThreadStarters: 10,
        fileAttachments: 0,
        maxFileAttachments: 10,
        webSearches: 0,
        maxWebSearches: 15,
        totalThreads: 0,
        maxTotalThreads: 50,
        monthlyChatMessages: 0,
        maxMonthlyChatMessages: 1200,
        resetPeriod: 'monthly',
      };
    
    case PricingTier.Pro:
      return {
        totalCanvases: 0,
        maxTotalCanvases: -1, // Unlimited
        threadStarters: 0,
        maxThreadStarters: -1, // Unlimited
        fileAttachments: 0,
        maxFileAttachments: -1, // Unlimited
        webSearches: 0,
        maxWebSearches: -1, // Unlimited
        totalThreads: 0,
        maxTotalThreads: -1, // Unlimited
        monthlyChatMessages: 0,
        maxMonthlyChatMessages: -1, // Unlimited
        resetPeriod: 'never', // No reset needed for unlimited
      };
    
    default:
      return getDefaultQuotaForTier(PricingTier.Free);
  }
};

// Usage tracking functions
export const incrementUsage = async (uid: string, usageType: UsageType): Promise<void> => {
  const quota = await getUserQuota(uid);
  if (!quota) {
    // Create default quota if it doesn't exist
    await createDefaultQuota(uid);
    return incrementUsage(uid, usageType);
  }
  
  const updates: Partial<UserQuota> = {
    [usageType]: quota[usageType] + 1,
  };
  
  await updateUserQuota(uid, updates);
};

export const decrementUsage = async (uid: string, usageType: UsageType): Promise<void> => {
  const quota = await getUserQuota(uid);
  if (!quota) {
    console.warn(`❌ [firestoreApi] No user quota found for user ${uid}, cannot decrement ${usageType}`);
    return;
  }
  
  const currentValue = quota[usageType];
  const newValue = Math.max(0, currentValue - 1); // Prevent negative values
  
  const updates: Partial<UserQuota> = {
    [usageType]: newValue,
  };
  
  await updateUserQuota(uid, updates);
};

// Check if user has exceeded quota
export const checkQuotaLimit = async (uid: string, usageType: UsageType): Promise<boolean> => {
  try {
    // Get user's tier limits from user quota
    const userQuota = await getUserQuota(uid);
    if (!userQuota) {
      console.warn(`❌ [firestoreApi] No user quota found for user ${uid}, allowing usage`);
      return false; // No quota set, allow usage
    }
    
    const maxKey = USAGE_TO_MAX_KEY_MAP[usageType];
    const maxValue = userQuota[maxKey];
    
    // -1 means unlimited
    if (maxValue === -1) {
      return false;
    }
    
    // Get current usage from user quota
    const currentUsage = userQuota[usageType];
    
    const hasExceeded = currentUsage >= maxValue;
    
    return hasExceeded;
  } catch (error) {
    console.error(`❌ [firestoreApi] Error checking quota limit for ${usageType}:`, error);
    // On error, allow usage to prevent blocking users
    return false;
  }
};

// Check if user has exceeded quota and return both result and quota data
export const checkQuotaLimitWithData = async (uid: string, usageType: UsageType): Promise<{ hasExceeded: boolean; userQuota: UserQuota | null }> => {
  try {
    // Get user's tier limits from user quota
    const userQuota = await getUserQuota(uid);
    if (!userQuota) {
      console.warn(`❌ [firestoreApi] No user quota found for user ${uid}, allowing usage`);
      return { hasExceeded: false, userQuota: null }; // No quota set, allow usage
    }
    
    const maxKey = USAGE_TO_MAX_KEY_MAP[usageType];
    const maxValue = userQuota[maxKey];
    
    // -1 means unlimited
    if (maxValue === -1) {
      return { hasExceeded: false, userQuota };
    }
    
    // Get current usage from user quota
    const currentUsage = userQuota[usageType];
    
    const hasExceeded = currentUsage >= maxValue;
    
    return { hasExceeded, userQuota };
  } catch (error) {
    console.error(`❌ [firestoreApi] Error checking quota limit for ${usageType}:`, error);
    // On error, allow usage to prevent blocking users
    return { hasExceeded: false, userQuota: null };
  }
};

// Special quota check for thread ideas that checks per parent note (each note gets its own quota)
export const checkThreadIdeaQuotaForParentNote = async (uid: string, parentNoteId: string, branchRelationships: Array<{parentId: string, branchId: string}>): Promise<boolean> => {
  try {
    // Get user's tier limits
    const userQuota = await getUserQuota(uid);
    if (!userQuota) {
      console.warn(`❌ [firestoreApi] No user quota found for user ${uid}, allowing thread idea creation`);
      return false; // No quota set, allow usage
    }
    
    const maxTotalThreads = userQuota.maxTotalThreads;
    
    // -1 means unlimited
    if (maxTotalThreads === -1) {
      return false;
    }
    
    // Get current total threads usage
    const currentTotalThreads = userQuota.totalThreads;
    
    const hasExceeded = currentTotalThreads >= maxTotalThreads;
    
    return hasExceeded;
  } catch (error) {
    console.error(`❌ [firestoreApi] Error checking thread idea quota for parent note:`, error);
    // On error, allow usage to prevent blocking users
    return false;
  }
};

// Canvas quota functions removed - no longer needed with user-level only quota system

// Canvas quota aggregation removed - no longer needed with user-level only quota system

// Special quota check for canvas creation (global, not per-canvas)
// DEPRECATED: Use checkQuotaLimitLocal(userId, 'totalCanvases') from quotaCache instead for better performance
export const checkCanvasCreationQuota = async (uid: string): Promise<boolean> => {
  try {
    // Get user's tier limits
    const userQuota = await getUserQuota(uid);
    if (!userQuota) {
      console.warn(`❌ [firestoreApi] No user quota found for user ${uid}, allowing canvas creation`);
      return false; // No quota set, allow usage
    }
    
    const maxTotalCanvases = userQuota.maxTotalCanvases;
    const currentTotalCanvases = userQuota.totalCanvases;
    
    // -1 means unlimited
    if (maxTotalCanvases === -1) {
      return false;
    }
    
    const hasExceeded = currentTotalCanvases >= maxTotalCanvases;
    
    return hasExceeded;
  } catch (error) {
    console.error(`❌ [firestoreApi] Error checking canvas creation quota:`, error);
    // On error, allow usage to prevent blocking users
    return false;
  }
};

// Helper function to find the root note for a given note ID
export const findRootNoteId = (noteId: string, notes: Array<{id: string, parentId?: string}>): string => {
  const note = notes.find(n => n.id === noteId);
  if (!note || !note.parentId) {
    return noteId; // This is already a root note
  }
  
  // Recursively find the root
  return findRootNoteId(note.parentId, notes);
};

// Helper function to count thread ideas for a specific root note
export const countThreadIdeasForRootNote = (rootNoteId: string, branchRelationships: Array<{parentId: string, branchId: string}>): number => {
  // Get all branch relationships that belong to this root note tree
  const getAllDescendants = (parentId: string): string[] => {
    const directChildren = branchRelationships
      .filter(rel => rel.parentId === parentId)
      .map(rel => rel.branchId);
    
    // Recursively get all descendants
    const allDescendants = [...directChildren];
    for (const childId of directChildren) {
      allDescendants.push(...getAllDescendants(childId));
    }
    
    return allDescendants;
  };
  
  const allDescendants = getAllDescendants(rootNoteId);
  const count = allDescendants.length;
  
  return count;
};

// Migration functions removed - no longer needed with new simplified quota structure

// Re-export types for convenience
export type { UserProfile, UserSubscription, UserQuota };
